import type { PublicKey } from '@shared/types';
import { base64ToBytes, bytesToBase64, toArrayBuffer, utf8ToBytes, bytesToUtf8 } from '../utils/encoding';
import { decryptMessageAsyncWithKey, encryptMessageAsyncWithKey } from '../crypto/messageEncryption';

const GROUP_KEY_ENVELOPE_PREFIX = 'gk2:';
const GROUP_CIPHERTEXT_PREFIX = 'gcm1:';
const GROUP_KEY_BYTES = 32;
const GROUP_NONCE_BYTES = 12;

export function isGroupCiphertext(ciphertext: string | null | undefined): boolean {
  return typeof ciphertext === 'string' && ciphertext.startsWith(GROUP_CIPHERTEXT_PREFIX);
}

export async function computeGroupKeyCommitment(rawKey: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', toArrayBuffer(rawKey));
  const bytes = new Uint8Array(digest);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function parseGroupKeyEnvelope(envelopeCiphertext: string): { publisherIdentityId: string; ciphertext: string } | null {
  if (!envelopeCiphertext.startsWith(GROUP_KEY_ENVELOPE_PREFIX)) return null;
  const rest = envelopeCiphertext.slice(GROUP_KEY_ENVELOPE_PREFIX.length);
  const idx = rest.indexOf(':');
  if (idx <= 0) return null;
  const publisherIdentityId = rest.slice(0, idx).trim();
  const ciphertext = rest.slice(idx + 1);
  if (!publisherIdentityId || !ciphertext) return null;
  return { publisherIdentityId, ciphertext };
}

export async function encryptGroupKeyEnvelope(params: {
  rawKey: Uint8Array;
  publisherIdentityId: string;
  publisherEcdhKey: CryptoKey | null;
  publisherEcdhScalar: Uint8Array;
  recipientPublicKey: PublicKey;
}): Promise<string> {
  if (params.rawKey.length !== GROUP_KEY_BYTES) {
    throw new Error(`Group key must be ${GROUP_KEY_BYTES} bytes`);
  }
  const plaintext = bytesToBase64(params.rawKey);
  const ciphertext = await encryptMessageAsyncWithKey(
    plaintext,
    params.recipientPublicKey,
    params.publisherEcdhKey,
    params.publisherEcdhScalar
  );
  return `${GROUP_KEY_ENVELOPE_PREFIX}${params.publisherIdentityId}:${ciphertext}`;
}

export async function decryptGroupKeyEnvelope(params: {
  envelopeCiphertext: string;
  publisherPublicKey: PublicKey;
  recipientEcdhKey: CryptoKey | null;
  recipientEcdhScalar: Uint8Array;
}): Promise<Uint8Array | null> {
  const parsed = parseGroupKeyEnvelope(params.envelopeCiphertext);
  if (!parsed) return null;
  const plaintext = await decryptMessageAsyncWithKey(
    parsed.ciphertext,
    params.publisherPublicKey,
    params.recipientEcdhKey,
    params.recipientEcdhScalar
  );
  if (!plaintext) return null;
  const raw = base64ToBytes(plaintext);
  if (raw.length !== GROUP_KEY_BYTES) return null;
  return raw;
}

export function generateGroupKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(GROUP_KEY_BYTES));
}

async function importGroupKey(rawKey: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', toArrayBuffer(rawKey), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

export async function encryptGroupMessage(plaintext: string, rawKey: Uint8Array): Promise<string> {
  const key = await importGroupKey(rawKey);
  const nonce = crypto.getRandomValues(new Uint8Array(GROUP_NONCE_BYTES));
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: toArrayBuffer(nonce) }, key, toArrayBuffer(utf8ToBytes(plaintext)));
  const payload = new Uint8Array(nonce.length + encrypted.byteLength);
  payload.set(nonce, 0);
  payload.set(new Uint8Array(encrypted), nonce.length);
  return `${GROUP_CIPHERTEXT_PREFIX}${bytesToBase64(payload)}`;
}

export async function decryptGroupMessage(ciphertext: string, rawKey: Uint8Array): Promise<string | null> {
  if (!isGroupCiphertext(ciphertext)) return null;
  const payload = base64ToBytes(ciphertext.slice(GROUP_CIPHERTEXT_PREFIX.length));
  if (payload.length <= GROUP_NONCE_BYTES) return null;
  const nonce = payload.slice(0, GROUP_NONCE_BYTES);
  const encrypted = payload.slice(GROUP_NONCE_BYTES);
  try {
    const key = await importGroupKey(rawKey);
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: toArrayBuffer(nonce) }, key, toArrayBuffer(encrypted));
    return bytesToUtf8(new Uint8Array(decrypted));
  } catch {
    return null;
  }
}
